import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, PackagePlus, AlertTriangle, Package, Search, FileDown, FileUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const UNITS = ["gr", "kg", "ml", "l", "pieza"];

const empty = {
  name: "", unit: "gr", stock: 0, min_stock: 0,
  cost_per_unit: 0, supplier: "", expires_at: "",
};

export default function AdminInventory() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("ing");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | low | out | ok
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // Restock
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockTarget, setRestockTarget] = useState(null);
  const [restockForm, setRestockForm] = useState({ quantity: "", cost_per_unit: "", supplier: "", expires_at: "" });
  // Adjust / Waste
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ quantity: "", reason: "waste", note: "" });
  // Excel import
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/ingredients/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "oRdeN-Inventario-Plantilla.xlsx";
      a.rel = "noopener";
      a.target = "_self";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        window.URL.revokeObjectURL(url);
      }, 1500);
      toast.success("Plantilla descargada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo descargar");
    }
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Elige un archivo .xlsx"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/admin/ingredients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      const c = data.created_count || 0;
      const u = data.updated_count || 0;
      const e = data.error_count || 0;
      toast.success(`${c} nuevo(s) · ${u} actualizado(s)${e ? ` · ${e} con error` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ingredients");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name, unit: it.unit, stock: it.stock, min_stock: it.min_stock,
      cost_per_unit: it.cost_per_unit, supplier: it.supplier || "", expires_at: it.expires_at || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (!form.unit) return toast.error("Unidad requerida");
    const payload = {
      name: form.name.trim(),
      unit: form.unit,
      stock: Number(form.stock) || 0,
      min_stock: Number(form.min_stock) || 0,
      cost_per_unit: Number(form.cost_per_unit) || 0,
      supplier: form.supplier || "",
      expires_at: form.expires_at || null,
    };
    try {
      if (editing) {
        await api.put(`/admin/ingredients/${editing.id}`, payload);
        toast.success("Ingrediente actualizado");
      } else {
        await api.post("/admin/ingredients", payload);
        toast.success("Ingrediente creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/ingredients/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitRestock = async () => {
    const qty = Number(restockForm.quantity);
    if (!qty || qty <= 0) return toast.error("Cantidad inválida");
    const payload = {
      quantity: qty,
      cost_per_unit: restockForm.cost_per_unit ? Number(restockForm.cost_per_unit) : null,
      supplier: restockForm.supplier || null,
      expires_at: restockForm.expires_at || null,
    };
    try {
      await api.post(`/admin/ingredients/${restockTarget.id}/restock`, payload);
      toast.success(`+${qty} ${restockTarget.unit} a stock`);
      setRestockOpen(false);
      setRestockForm({ quantity: "", cost_per_unit: "", supplier: "", expires_at: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitAdjust = async () => {
    const qty = Number(adjustForm.quantity);
    if (qty === 0 || isNaN(qty)) return toast.error("Cantidad inválida");
    // For waste, force negative
    const signed = adjustForm.reason === "waste" ? -Math.abs(qty) : qty;
    try {
      await api.post(`/admin/ingredients/${adjustTarget.id}/adjust`, {
        quantity: signed, reason: adjustForm.reason, note: adjustForm.note || "",
      });
      toast.success("Movimiento registrado");
      setAdjustOpen(false);
      setAdjustForm({ quantity: "", reason: "waste", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const stockBadge = (it) => {
    if (Number(it.stock) <= 0) return <Badge className="bg-[#e11d48] hover:bg-[#e11d48]/80">Sin stock</Badge>;
    if (it.min_stock > 0 && Number(it.stock) <= Number(it.min_stock))
      return <Badge className="bg-[#f59e0b] hover:bg-[#f59e0b]/80">Bajo mínimo</Badge>;
    return <Badge className="bg-[#10b981] hover:bg-[#10b981]/80">OK</Badge>;
  };

  const totalValue = items.reduce(
    (acc, it) => acc + Number(it.stock || 0) * Number(it.cost_per_unit || 0), 0
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(it => {
      if (q && !(it.name?.toLowerCase().includes(q) || (it.supplier || "").toLowerCase().includes(q))) return false;
      if (statusFilter === "out") return Number(it.stock) <= 0;
      if (statusFilter === "low") return it.min_stock > 0 && Number(it.stock) > 0 && Number(it.stock) <= Number(it.min_stock);
      if (statusFilter === "ok") return Number(it.stock) > Number(it.min_stock || 0);
      return true;
    });
  }, [items, search, statusFilter]);

  // Deep-link highlight: scroll to and pulse the targeted ingredient
  useEffect(() => {
    if (!highlightId || items.length === 0) return;
    const node = document.querySelector(`[data-testid="ingredient-row-${highlightId}"]`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.add("ring-2", "ring-[#e11d48]", "ring-offset-2");
      const t = setTimeout(() => {
        node.classList.remove("ring-2", "ring-[#e11d48]", "ring-offset-2");
        // Drop the param after fading
        const next = new URLSearchParams(searchParams);
        next.delete("ing");
        setSearchParams(next, { replace: true });
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [highlightId, items, searchParams, setSearchParams]);

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1a0f2e]">Inventario</h1>
          <p className="text-[#7248ae] text-sm">Materias primas e ingredientes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="px-4 py-2 rounded-xl bg-white border border-[#b182ec]/30 text-right">
            <p className="text-[10px] uppercase tracking-widest text-[#7248ae]">Valor total</p>
            <p className="font-display text-lg text-[#1a0f2e]" data-testid="inventory-total-value">
              {formatCurrency(totalValue)}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={downloadTemplate}
            disabled={expired}
            className="border-[#b182ec]/50"
            data-testid="inventory-template-button"
          >
            <FileDown className="h-4 w-4 mr-2" />Plantilla
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }}
            disabled={expired}
            className="border-[#b182ec]/50"
            data-testid="inventory-import-button"
          >
            <FileUp className="h-4 w-4 mr-2" />Importar Excel
          </Button>
          <Button
            disabled={expired}
            onClick={startCreate}
            className="bg-[#3e166c] hover:bg-[#3e166c]/90"
            data-testid="new-ingredient-button"
          >
            <Plus className="h-4 w-4 mr-2" />Nuevo
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="p-8 text-center text-[#7248ae]">Cargando…</Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-[#b182ec]/40 bg-white">
          <Package className="h-10 w-10 mx-auto text-[#7248ae] mb-3" />
          <p className="text-[#1a0f2e] font-display text-xl">Sin ingredientes</p>
          <p className="text-sm text-[#7248ae] mt-1">Crea tu primer ingrediente para empezar a controlar stock y predicciones.</p>
        </Card>
      ) : (
        <>
          {/* Filter bar */}
          <div className="flex flex-col sm:flex-row gap-2" data-testid="inventory-filter-bar">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7248ae]" />
              <Input
                placeholder="Buscar por nombre o proveedor…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="inventory-search-input"
              />
            </div>
            <div className="flex gap-1 overflow-x-auto" data-testid="inventory-status-filter">
              {[
                { id: "all", label: "Todos" },
                { id: "low", label: "Bajo mínimo" },
                { id: "out", label: "Sin stock" },
                { id: "ok", label: "OK" },
              ].map(s => (
                <Button
                  key={s.id}
                  size="sm"
                  variant={statusFilter === s.id ? "default" : "outline"}
                  onClick={() => setStatusFilter(s.id)}
                  className={statusFilter === s.id ? "bg-[#3e166c] hover:bg-[#3e166c]/90 whitespace-nowrap" : "whitespace-nowrap"}
                  data-testid={`filter-${s.id}`}
                >
                  {s.label}
                </Button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <Card className="p-8 text-center text-[#7248ae]" data-testid="inventory-empty-filtered">
              No hay ingredientes que coincidan con el filtro.
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3" data-testid="inventory-list">
              {filtered.map(it => (
                <Card key={it.id} className="p-4 bg-white border-[#b182ec]/30 transition-shadow" data-testid={`ingredient-row-${it.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg text-[#1a0f2e] truncate">{it.name}</h3>
                    {stockBadge(it)}
                  </div>
                  <p className="text-xs text-[#7248ae] mt-1">
                    {it.supplier ? `Proveedor: ${it.supplier}` : "Sin proveedor"}
                    {it.expires_at ? ` · Caduca: ${it.expires_at.slice(0, 10)}` : ""}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3 md:gap-6 text-sm">
                  <div>
                    <p className="text-[10px] uppercase text-[#7248ae] tracking-widest">Stock</p>
                    <p className="font-display text-lg text-[#1a0f2e]">{it.stock} <span className="text-xs text-[#7248ae]">{it.unit}</span></p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#7248ae] tracking-widest">Mín.</p>
                    <p className="font-display text-lg text-[#1a0f2e]">{it.min_stock} <span className="text-xs text-[#7248ae]">{it.unit}</span></p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#7248ae] tracking-widest">Costo/u</p>
                    <p className="font-display text-lg text-[#1a0f2e]">{formatCurrency(it.cost_per_unit)}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm" variant="outline" disabled={expired}
                    onClick={() => { setRestockTarget(it); setRestockOpen(true); }}
                    className="border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10"
                    data-testid={`restock-button-${it.id}`}
                  >
                    <PackagePlus className="h-4 w-4 mr-1" />Reabastecer
                  </Button>
                  <Button
                    size="sm" variant="outline" disabled={expired}
                    onClick={() => { setAdjustTarget(it); setAdjustOpen(true); }}
                    className="border-[#f59e0b]/40 text-[#f59e0b] hover:bg-[#f59e0b]/10"
                    data-testid={`adjust-button-${it.id}`}
                  >
                    <AlertTriangle className="h-4 w-4 mr-1" />Merma
                  </Button>
                  <Button size="sm" variant="outline" disabled={expired} onClick={() => startEdit(it)} data-testid={`edit-ingredient-${it.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" disabled={expired} className="text-[#e11d48] border-[#e11d48]/30" data-testid={`delete-ingredient-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar ingrediente</AlertDialogTitle>
                        <AlertDialogDescription>
                          Se quitará de las recetas que lo usen. Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(it.id)} className="bg-[#e11d48] hover:bg-[#e11d48]/90">Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ingredient-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar ingrediente" : "Nuevo ingrediente"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej. Tomate Saladette" data-testid="ingredient-name-input" />
            </div>
            <div>
              <Label>Unidad</Label>
              <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="ingredient-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Costo unitario (MXN)</Label>
              <Input type="number" step="0.01" value={form.cost_per_unit} onChange={e => setForm({ ...form, cost_per_unit: e.target.value })} data-testid="ingredient-cost-input" />
            </div>
            {!editing && (
              <div>
                <Label>Stock inicial</Label>
                <Input type="number" step="0.01" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} data-testid="ingredient-stock-input" />
              </div>
            )}
            <div>
              <Label>Stock mínimo</Label>
              <Input type="number" step="0.01" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} data-testid="ingredient-min-stock-input" />
            </div>
            <div className="col-span-2">
              <Label>Proveedor (opcional)</Label>
              <Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} data-testid="ingredient-supplier-input" />
            </div>
            <div className="col-span-2">
              <Label>Fecha de caducidad (opcional)</Label>
              <Input type="date" value={form.expires_at ? String(form.expires_at).slice(0, 10) : ""} onChange={e => setForm({ ...form, expires_at: e.target.value })} data-testid="ingredient-expires-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#3e166c] hover:bg-[#3e166c]/90" data-testid="ingredient-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restock dialog */}
      <Dialog open={restockOpen} onOpenChange={setRestockOpen}>
        <DialogContent className="max-w-md" data-testid="restock-dialog">
          <DialogHeader>
            <DialogTitle>Reabastecer · {restockTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cantidad ({restockTarget?.unit})</Label>
              <Input type="number" step="0.01" value={restockForm.quantity} onChange={e => setRestockForm({ ...restockForm, quantity: e.target.value })} data-testid="restock-qty-input" />
            </div>
            <div>
              <Label>Costo unitario (opcional, MXN)</Label>
              <Input type="number" step="0.01" value={restockForm.cost_per_unit} onChange={e => setRestockForm({ ...restockForm, cost_per_unit: e.target.value })} data-testid="restock-cost-input" />
            </div>
            <div>
              <Label>Proveedor (opcional)</Label>
              <Input value={restockForm.supplier} onChange={e => setRestockForm({ ...restockForm, supplier: e.target.value })} />
            </div>
            <div>
              <Label>Caducidad (opcional)</Label>
              <Input type="date" value={restockForm.expires_at} onChange={e => setRestockForm({ ...restockForm, expires_at: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestockOpen(false)}>Cancelar</Button>
            <Button onClick={submitRestock} className="bg-[#10b981] hover:bg-[#10b981]/90" data-testid="restock-submit-button">Registrar entrada</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust / Waste dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-md" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Merma / Ajuste · {adjustTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={adjustForm.reason} onValueChange={v => setAdjustForm({ ...adjustForm, reason: v })}>
                <SelectTrigger data-testid="adjust-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="waste">Merma / Desperdicio</SelectItem>
                  <SelectItem value="adjustment">Ajuste manual (+/-)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{adjustForm.reason === "waste" ? "Cantidad perdida" : "Cantidad (usa - para restar)"} ({adjustTarget?.unit})</Label>
              <Input type="number" step="0.01" value={adjustForm.quantity} onChange={e => setAdjustForm({ ...adjustForm, quantity: e.target.value })} data-testid="adjust-qty-input" />
            </div>
            <div>
              <Label>Nota (opcional)</Label>
              <Input value={adjustForm.note} onChange={e => setAdjustForm({ ...adjustForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancelar</Button>
            <Button onClick={submitAdjust} className="bg-[#f59e0b] hover:bg-[#f59e0b]/90" data-testid="adjust-submit-button">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-white" data-testid="inventory-import-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1a0f2e]">Importar inventario desde Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#7248ae]">
              Sube el archivo <code className="font-mono text-[#3e166c]">.xlsx</code> con tu inventario.
              Si el nombre del ingrediente ya existe, se <strong>actualiza</strong>. Si no existe, se <strong>crea</strong>.
              ¿No tienes plantilla? Descárgala primero arriba.
            </p>
            <Input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              data-testid="inventory-import-file"
            />
            {importResult && (
              <div className="text-xs text-[#1a0f2e] bg-[#fcfbfd] border border-[#b182ec]/30 rounded-xl p-3 space-y-1" data-testid="inventory-import-result">
                <p>✅ Creados: <strong>{importResult.created_count}</strong></p>
                <p>♻️ Actualizados: <strong>{importResult.updated_count}</strong></p>
                {importResult.error_count > 0 && (
                  <>
                    <p>⚠️ Errores: <strong>{importResult.error_count}</strong></p>
                    <ul className="list-disc pl-5 text-rose-700">
                      {importResult.errors.slice(0, 6).map((e, i) => (
                        <li key={i}>Fila {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setImportOpen(false)} className="border-[#b182ec]/50">Cerrar</Button>
            <Button
              onClick={runImport}
              disabled={!importFile || importing}
              className="bg-[#3e166c] hover:bg-[#3e166c]/90"
              data-testid="inventory-import-submit"
            >
              {importing ? "Importando…" : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
